import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calculator, ArrowRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYP_OPTIONS = [
  { value: "haus", label: "Haus" },
  { value: "wohnung", label: "Wohnung" },
  { value: "grundstueck", label: "Grundstück" },
];

const formatEUR = (n) =>
  new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(n);

export default function EstimateWidget() {
  const [typ, setTyp] = useState("haus");
  const [groesse, setGroesse] = useState("");
  const [ort, setOrt] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!groesse || !ort) {
      toast.error("Bitte Größe und Ort angeben.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/estimate`, {
        typ,
        groesse: parseFloat(groesse),
        ort,
        email: email || null,
      });
      setResult(data);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        "Schätzung fehlgeschlagen. Bitte erneut versuchen.";
      toast.error(typeof msg === "string" ? msg : "Schätzung fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setResult(null);
    setGroesse("");
    setOrt("");
    setEmail("");
  };

  return (
    <section
      id="schaetzung"
      data-testid="estimate-section"
      className="bg-white py-24 lg:py-28 border-b border-neutral-200"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#8A151B]" />
              <span className="text-xs uppercase tracking-[0.3em] font-bold text-[#8A151B]">
                Schätzung in 30 Sekunden
              </span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05] text-[#0A0A0A]">
              Was ist Ihre
              <br />
              Immobilie wert?
            </h2>
            <p className="mt-6 text-base lg:text-lg text-neutral-600 leading-relaxed max-w-md">
              Drei Angaben — eine erste Einschätzung für Südhessen. Kein
              Verkaufsdruck, keine versteckten Kosten. Wenn Sie mehr wissen
              möchten, melde ich mich persönlich.
            </p>
          </div>

          <div className="lg:col-span-7">
            <div className="border border-neutral-200 bg-[#F5F5F5] p-8 lg:p-10">
              {result ? (
                <div data-testid="estimate-result" className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#8A151B] flex-shrink-0 flex items-center justify-center">
                      <Calculator size={22} className="text-white" />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-[0.25em] font-bold text-neutral-500">
                        Geschätzter Marktwert
                      </p>
                      <p className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A0A0A] mt-2 leading-none tracking-tighter">
                        {formatEUR(result.min_price)}
                        <span className="text-neutral-400 mx-3">–</span>
                        {formatEUR(result.max_price)}
                      </p>
                      <p className="mt-3 text-sm text-neutral-600">
                        Für {result.groesse} m² {TYP_OPTIONS.find((t) => t.value === result.typ)?.label} in {result.ort}.
                      </p>
                    </div>
                  </div>

                  <div className="border-t border-neutral-300 pt-6 text-xs text-neutral-500 leading-relaxed">
                    Diese Schätzung ist eine grobe Spanne auf Basis von
                    Durchschnittspreisen für Südhessen. Für eine fundierte
                    Bewertung Ihrer konkreten Immobilie (Lage, Zustand,
                    Ausstattung) komme ich gerne persönlich vorbei.
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 pt-2">
                    <a
                      href="#kontakt"
                      data-testid="estimate-cta-contact"
                      className="inline-flex items-center justify-center gap-2 bg-[#8A151B] text-white hover:bg-[#6B1015] px-6 py-3 font-semibold tracking-wide transition-all duration-300 uppercase text-sm"
                    >
                      Persönliche Bewertung anfragen
                      <ArrowRight size={16} />
                    </a>
                    <button
                      onClick={reset}
                      data-testid="estimate-reset"
                      className="inline-flex items-center justify-center gap-2 bg-transparent border border-neutral-400 text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white px-6 py-3 font-semibold tracking-wide transition-colors duration-300 uppercase text-sm"
                    >
                      Neu berechnen
                    </button>
                  </div>
                </div>
              ) : (
                <form
                  onSubmit={submit}
                  data-testid="estimate-form"
                  className="space-y-6"
                >
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] font-bold text-neutral-500 block mb-3">
                      Objektart
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {TYP_OPTIONS.map((t) => (
                        <button
                          type="button"
                          key={t.value}
                          data-testid={`estimate-typ-${t.value}`}
                          onClick={() => setTyp(t.value)}
                          className={`px-2 sm:px-4 py-3 text-xs sm:text-sm font-semibold uppercase tracking-wide border transition-all duration-200 whitespace-nowrap ${
                            typ === t.value
                              ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                              : "bg-white text-[#0A0A0A] border-neutral-300 hover:border-[#0A0A0A]"
                          }`}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="text-xs uppercase tracking-[0.2em] font-bold text-neutral-500 block mb-3">
                        Größe (m²)
                      </label>
                      <input
                        data-testid="estimate-input-groesse"
                        type="number"
                        min="11"
                        max="9999"
                        value={groesse}
                        onChange={(e) => setGroesse(e.target.value)}
                        placeholder="z.B. 140"
                        className="w-full bg-white border border-neutral-300 px-4 py-3 text-[#0A0A0A] placeholder:text-neutral-400 focus:outline-none focus:border-[#8A151B] transition-colors text-base"
                        required
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-[0.2em] font-bold text-neutral-500 block mb-3">
                        Ort / PLZ
                      </label>
                      <input
                        data-testid="estimate-input-ort"
                        type="text"
                        value={ort}
                        onChange={(e) => setOrt(e.target.value)}
                        placeholder="z.B. Rödermark"
                        className="w-full bg-white border border-neutral-300 px-4 py-3 text-[#0A0A0A] placeholder:text-neutral-400 focus:outline-none focus:border-[#8A151B] transition-colors text-base"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] font-bold text-neutral-500 block mb-3">
                      E-Mail (optional)
                    </label>
                    <input
                      data-testid="estimate-input-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Damit ich mich melden kann"
                      className="w-full bg-white border border-neutral-300 px-4 py-3 text-[#0A0A0A] placeholder:text-neutral-400 focus:outline-none focus:border-[#8A151B] transition-colors text-base"
                    />
                  </div>

                  <button
                    type="submit"
                    data-testid="estimate-submit"
                    disabled={loading}
                    className="group w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#8A151B] text-white hover:bg-[#6B1015] disabled:opacity-60 disabled:cursor-not-allowed px-10 py-4 font-semibold tracking-wide transition-all duration-300 uppercase text-sm"
                  >
                    {loading ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Wird berechnet…
                      </>
                    ) : (
                      <>
                        Wert kostenlos schätzen
                        <ArrowRight
                          size={16}
                          className="group-hover:translate-x-1 transition-transform"
                        />
                      </>
                    )}
                  </button>

                  <p className="text-xs text-neutral-500 leading-relaxed">
                    Unverbindlich. Keine Anmeldung erforderlich. Die
                    Schätzung basiert auf regionalen Durchschnittspreisen
                    und ersetzt kein professionelles Gutachten.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
